// Shared assignment placeholder expansion — single source of truth for
// Reminders (SMS) and Send Details (Email) template bodies.
// Extracted 2026-09-18 from InterpretationReminders.jsx +
// InterpretationSendDetails.jsx (was duplicated in both files).
import dayjs from "dayjs";
import { normalizePhoneNumber } from "helpers/ServiceHelpers";

// Placeholder mapping for assignment data
export const PLACEHOLDER_MAP = {
  ClientName: "Client?.Account",
  ClientAddress: "Client?.Address",
  LinguistName: "Linguist?.ShortName||Linguist?.Name||LinguistName",
  LinguistNumber: "Linguist?.PhoneNumber",
  LinguistAddress: "Linguist?.Address",
  AssignmentName: "Name",
  AssignmentDuration: "Duration",
  AssignmentStartTime: "TimeStart",
  AssignmentEndTime: "TimeEnd",
  AssignmentDate: "Date",
  AssignmentDetails: "Details||Name",
  AssignmentRequestor: "Requestor?.Name",
  // Multi-contact person support
  AssignmentContactPerson: "ContactPersons.0.Name||ContactPersonName",
  AssignmentContactPersonNumber:
    "ContactPersons.0.PhoneNumber||ContactPersonNumber",
  AssignmentContactPersonEmail: "ContactPersons.0.Email",
  AssignmentContactPersonExt: "ContactPersons.0.Ext",
  AssignmentType: "ServiceSubtype?.Name||InterpretationType",
  AssignmentLanguage: "ServiceTargetLanguages.0.Language.Name",
  AssignmentAddressLink: "Address",
  AssignmentPrepTime: "PreparationTime",
  Default: "NO_DATA_REPLACE_MANUALLY",
};

export const formatTimeToHHMM12Hr = (timeStr) => {
  if (!timeStr) return "";
  // Handle format "HH:MM:SS" or "HH:MM"
  const timeParts = timeStr.split(":");
  if (!timeParts[0] || !timeParts[1]) return timeStr;

  let hours = parseInt(timeParts[0], 10);
  const minutes = timeParts[1];
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${hours}:${minutes} ${ampm}`;
};

export const formatDurationToHHMM = (durationStr) => {
  if (!durationStr) return "";
  // Handle format "HH:MM:SS" or "HH:MM"
  const parts = durationStr.split(":");
  if (!parts[0] || !parts[1]) return durationStr;

  return `${parts[0]}:${parts[1]}`;
};

export const replacePlaceholders = (text, assignmentData) => {
  if (!text) return "";

  let result = text;
  const placeholderRegex = /\{([^}]+)\}/g;

  result = result.replace(placeholderRegex, (match, placeholder) => {
    // Handle nested properties (e.g., Client?.Account)
    const path = PLACEHOLDER_MAP[placeholder];
    if (!path) return match; // Return original if no mapping

    // Special handling for AssignmentContactPerson with multiple contacts
    if (placeholder === "AssignmentContactPerson") {
      const contactPersons = assignmentData?.ContactPersons || [];
      if (contactPersons.length > 1) {
        // Format all contact persons when there are multiple
        const formatted = contactPersons
          .map(
            (cp, i) =>
              `${i + 1}. ${cp.Name}${
                cp.PhoneNumber
                  ? ` (${normalizePhoneNumber(cp.PhoneNumber)}${
                      cp.Ext ? ` ext. ${cp.Ext}` : ""
                    })`
                  : ""
              }`,
          )
          .join("<br/>");
        return formatted;
      }
      // Fall through to normal path resolution for single contact
    }

    const pathOptions = path.split("||").map((p) => p.trim());
    let value = null;

    for (const option of pathOptions) {
      const keys = option.split("?").join(".").split(".");
      let currentValue = assignmentData;

      for (const key of keys) {
        if (key && currentValue) {
          currentValue = currentValue[key];
        }
      }

      if (
        currentValue !== undefined &&
        currentValue !== null &&
        currentValue !== ""
      ) {
        value = currentValue;
        break;
      }
    }

    if (value === undefined || value === null || value === "") {
      return '<strong style="color: red;">NO_DATA_REPLACE_MANUALLY</strong>';
    }

    // Apply special formatting for time fields
    if (
      placeholder === "AssignmentStartTime" ||
      placeholder === "AssignmentEndTime"
    ) {
      return formatTimeToHHMM12Hr(value);
    }

    if (placeholder === "AssignmentDuration") {
      return formatDurationToHHMM(value);
    }

    // Apply special formatting for date field
    if (placeholder === "AssignmentDate") {
      return dayjs.utc(value).format("dddd, MMMM DD, YYYY");
    }

    return (
      value || '<strong style="color: red;">NO_DATA_REPLACE_MANUALLY</strong>'
    );
  });

  return result;
};